import { useCallback, useEffect, useRef, useState } from "react";
import DropAssetModal from "domains/assets/components/DropAssetModal";
import { mapAssetTypeToExploreTab } from "domains/assets/utils/mapAssetTypeToExploreTab";
import FragmentCollectionTitle from "domains/collections/components/FragmentCollectionTitle";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import { extractFirstQueryParam } from "domains/commons/misc";
import ExploreList, {
  ExploreListHandle,
} from "domains/explore/components/ExploreList";
import { EXPLORE_TABS, ExploreTabId } from "domains/explore/constants";
import { FmFileImage } from "domains/file-manager/interfacesV2";
import ModalLibraryAssetSelect from "domains/image/components/ModalLibraryAssetSelect";
import { useCreateNewModelModalContext } from "domains/models/contexts/CreateNewModelModalProvider";
import useRouter from "domains/navigation/hooks/useRouter";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { extraTheme } from "domains/theme";
import Button, { IconButton } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { chunkQuery } from "infra/api/chunkQuery";
import { useHandleApiError } from "infra/api/error";
import {
  useGetCollectionsByCollectionIdQuery,
  usePutAssetsByCollectionIdMutation,
} from "infra/api/generated/api";

import {
  Center,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";

interface ExploreContentProps {
  context: "explore" | "library";
  collectionId?: string;
}

export default function ExploreContent({
  context,
  collectionId,
}: ExploreContentProps) {
  const router = useRouter();
  const { selectedProject, isReader } = useTeamContext();
  const { isUnsigned } = useUserContext();
  const { openUpload: openUploadModelModal } = useCreateNewModelModalContext();
  const { successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const [isDropAssetModalDisplayed, setIsDropAssetModalDisplayed] =
    useState(false);
  const {
    isOpen: isModalAddAssetOpen,
    onClose: onModalAddAssetClose,
    onOpen: onModalAddAssetOpen,
  } = useDisclosure();
  const initialIndex = extractFirstQueryParam(router.query.initialIndex);
  const exploreListHandleRef = useRef<ExploreListHandle>(null);
  const [addAssetToCollectionTrigger] = usePutAssetsByCollectionIdMutation();
  const [uploadKind, setUploadKind] = useState<"image" | "video" | undefined>(
    undefined
  );
  const [lastTabId, setLastTabId] = usePersistedState<ExploreTabId>(
    PersistedStateKey.LAST_EXPLORE_TAB,
    {
      defaultValue: "image",
      transform: (value) => {
        if (context === "explore" && value === "canvas") {
          return undefined;
        } else {
          return value;
        }
      },
    }
  );

  const tabId =
    extractFirstQueryParam<ExploreTabId>(router.query.tab, {
      validValues: [...EXPLORE_TABS],
    }) ?? undefined;

  const { collection } = useGetCollectionsByCollectionIdQuery(
    collectionId
      ? {
          projectId: selectedProject.id,
          collectionId: collectionId,
        }
      : skipToken,
    {
      selectFromResult: ({ data }) => {
        return {
          collection: data?.collection,
        };
      },
    }
  );

  // ----------------------------------

  const handleTabChange = useCallback(
    (tabId: ExploreTabId) => {
      setUploadKind(
        ["image", "video"].includes(tabId)
          ? (tabId as "image" | "video")
          : undefined
      );

      void router.push(
        collectionId
          ? {
              pathname: "/collections/[id]",
              query: {
                id: collectionId,
                tab: tabId,
              },
            }
          : {
              pathname: context === "library" ? "/assets" : "/explore",
              query: {
                tab: tabId,
              },
            }
      );
    },
    [router, collectionId, context]
  );

  const handleCardClick = useCallback(
    async (file: FmFileImage, index: number) => {
      // Track the first click on the Explore page
      Track(AnalyticsEvents.Explore.ClickedAsset, {
        level: 1,
        assetId: file.id,
        assetKind: file.meta.metadata.kind,
        assetType: file.meta.metadata.type,
        assetSource: file.meta.source,
        assetModel: file.meta.metadata.modelId,
        assetModelType: file.meta.metadata.modelType,
      });

      await router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            initialIndex: String(index),
          },
        },
        "",
        { shallow: true }
      );
      await router.push({
        pathname: "/explore/asset/[id]",
        query: {
          id: file.id,
        },
      });
    },
    [router]
  );

  const handleBeforeUploading = useCallback(async () => {
    handleTabChange(uploadKind ?? "image");
    exploreListHandleRef.current?.goToUploaded();
  }, [handleTabChange, uploadKind]);

  const handleInitialFirstRender = useCallback(() => {
    // setModalStatus("close");
  }, []);

  const handleLibrarySelect = useCallback(
    async (assets: FmFileImage[]) => {
      if (!collectionId) return;

      const addedAssets = assets.filter(
        (asset) => !asset.meta.collectionIds.includes(collectionId)
      );

      if (!addedAssets.length) return;

      const assetIds = addedAssets.map((asset) => asset.id);
      const chunkedTrigger = chunkQuery(
        addAssetToCollectionTrigger,
        "body.assetIds",
        49
      );

      const nextTab = mapAssetTypeToExploreTab(
        addedAssets[0].meta.metadata.type
      );

      try {
        await chunkedTrigger({
          projectId: selectedProject.id,
          collectionId,
          body: {
            assetIds,
          },
        }).unwrap();

        handleTabChange(nextTab);
        successToast({
          title: `${assetIds.length} ${
            assetIds.length > 1 ? "assets" : "asset"
          } added to collection`,
        });
      } catch (error: any) {
        handleApiError(
          {
            ...error,
            data: {
              ...error.data,
              reason: error.data?.reason.replace("asset"),
            },
          },
          `Error adding asset to collection`
        );
      }
    },
    [
      addAssetToCollectionTrigger,
      collectionId,
      handleApiError,
      handleTabChange,
      selectedProject.id,
      successToast,
    ]
  );

  // ----------------------------------

  useEffect(() => {
    if (!tabId) return;
    setLastTabId(tabId);
    setUploadKind(
      ["image", "video"].includes(tabId)
        ? (tabId as "image" | "video")
        : undefined
    );
  }, [tabId, setLastTabId]);

  useEffect(() => {
    if (!tabId) {
      void router.replace({
        query: {
          ...router.query,
          tab: lastTabId,
        },
      });
    }
  }, [router, tabId, lastTabId]);

  return (
    <>
      <VStack
        align="stretch"
        w="full"
        maxW="1650px"
        mx="auto"
        px={9}
        py="60px"
        transition={extraTheme.transitions.fast}
        spacing="60px"
      >
        <VStack align="center" spacing={3}>
          <HStack pos="relative" justify="center" w="full" spacing={4}>
            {collectionId ? (
              <FragmentCollectionTitle collection={collection} />
            ) : (
              <Text lineHeight={1.2} size="title.2xl">
                {context === "library"
                  ? "Asset Hub"
                  : "Explore. Remix. Create."}
              </Text>
            )}

            {collectionId && (
              <Center
                pos={["relative", "relative", "absolute"]}
                top={0}
                bottom={0}
                left={0}
              >
                <IconButton
                  h="56px"
                  w="56px"
                  variant="secondaryAlt"
                  aria-label="Go back to Collections"
                  internalLink="/collections"
                >
                  <Icon id="Ui/ArrowLeft" h="18px" />
                </IconButton>
              </Center>
            )}

            {context === "library" &&
              !collectionId &&
              !isUnsigned &&
              !isReader && (
                <Center
                  pos={["relative", "relative", "absolute"]}
                  top={0}
                  right={0}
                  bottom={0}
                >
                  <Menu variant="blurred">
                    <MenuButton
                      as={Button}
                      leftIcon={<Icon id="Ui/Upload" h="14px" />}
                      rightIcon={<Icon id="Ui/ChevronDownSm" h="6px" />}
                      size="sm"
                      variant="secondary"
                    >
                      Upload
                    </MenuButton>

                    <MenuList w="130px">
                      <MenuItem
                        iconId="Nav/Image/Outline"
                        iconH="12px"
                        text="Image"
                        onClick={() => {
                          setUploadKind("image");
                          setIsDropAssetModalDisplayed(true);
                        }}
                      />

                      <MenuItem
                        iconId="Nav/Video/Outline"
                        iconH="12px"
                        text="Video"
                        onClick={() => {
                          setUploadKind("video");
                          setIsDropAssetModalDisplayed(true);
                        }}
                      />

                      <MenuItem
                        iconId="Nav/Model/Outline"
                        iconH="12px"
                        text="Model"
                        onClick={openUploadModelModal}
                      />
                    </MenuList>
                  </Menu>
                </Center>
              )}

            {collectionId && !isReader && (
              <Center
                pos={["relative", "relative", "absolute"]}
                top={0}
                right={0}
                bottom={0}
              >
                <Button
                  leftIcon={<Icon id="Ui/PlusSm" h="14px" />}
                  size="sm"
                  variant="secondary"
                  onClick={onModalAddAssetOpen}
                >
                  Add Asset
                </Button>
              </Center>
            )}
          </HStack>

          {!collectionId && (
            <Text color="textSecondary" lineHeight={1.2} size="body.2xl">
              {context === "library"
                ? "Your generated content and models, all in one place."
                : "Ready-to-use models and assets across every medium."}
            </Text>
          )}
        </VStack>

        {(tabId && context === "library" && !isUnsigned && (
          <DropAssetModal
            showModal={isDropAssetModalDisplayed}
            setShowModal={setIsDropAssetModalDisplayed}
            beforeUploading={handleBeforeUploading}
            collectionId={collectionId}
            kind={uploadKind ?? "image"}
            isDisabled={!uploadKind}
          >
            <ExploreList
              tabId={tabId}
              initialIndex={initialIndex ? parseInt(initialIndex) : undefined}
              onInitialFirstRender={handleInitialFirstRender}
              onTabChange={handleTabChange}
              onCardClick={handleCardClick}
              isWithHeader
              context="library"
              collectionId={collectionId}
              handleRef={exploreListHandleRef}
            />
          </DropAssetModal>
        )) ||
          (tabId && (
            <ExploreList
              tabId={tabId}
              initialIndex={initialIndex ? parseInt(initialIndex) : undefined}
              onInitialFirstRender={handleInitialFirstRender}
              onTabChange={handleTabChange}
              onCardClick={handleCardClick}
              context={context}
              isWithHeader
            />
          )) ||
          null}
      </VStack>

      {collectionId && !isReader && (
        <ModalLibraryAssetSelect
          isOpen={isModalAddAssetOpen}
          onClose={onModalAddAssetClose}
          onMultiSelect={handleLibrarySelect}
          tabs={["image", "3d", "video", "canvas", "texture", "skybox"]}
          isWithoutPublic
        />
      )}
    </>
  );
}
